import angularModule from 'Settings/angularModule/scripts/settings_module';
import template from 'Settings/angularModule/views/tuition_and_registration/cohort_registration_plan_selection.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { DISCUSS_OTHER_OPTIONS } from 'CohortSectionOffer';
import { paymentSituationWithFormattedValues } from 'PaymentSituation';
import { getAdmissionOffer } from 'Users';
import { getProduct } from 'Stripe';
import { isPartialScholarship } from 'Scholarship';
import { TuitionPlanFrequencySortKey, TuitionPlanFrequency } from 'TuitionPlan';
import { formatCurrencyForDisplay } from 'Currency';
import { getProgramConfigValue } from 'Program';
import { targetBrandConfig } from 'AppBranding';
import { additionalFinancingOptionWithFormattedValues } from './additionalFinancingOptionWithFormattedValues';
import { getDefaultSelectedPaymentOption, getPaymentConfig } from './getPaymentConfig';
import {
    attachCohortDatesFn,
    attachCohortRegistrationOptionsFn,
    attachDeferralModalFn,
    attachOpenStripeCheckoutSessionFn,
    attachPaymentConfigStateWatchFn,
    attachRegisterButtonWatchFn,
    attachRegisterEnabledWatchFn,
} from './CohortRegistrationDirHelpers';
import { PaymentTypeOption, PaymentView } from './TuitionAndRegistration.types';

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('cohortRegistrationPlanSelection', [
    '$injector',

    function factory($injector) {
        const $rootScope = $injector.get('$rootScope');
        const DialogModal = $injector.get('DialogModal');
        const EventLogger = $injector.get('EventLogger');
        const TranslationHelper = $injector.get('TranslationHelper');
        const ErrorLogService = $injector.get('ErrorLogService');
        const NavigationHelperMixin = $injector.get('Navigation.NavigationHelperMixin');
        const dateHelper = $injector.get('dateHelper');

        return {
            restrict: 'E',
            templateUrl,
            scope: {
                // This is called from inside the smallprint locale
                launchProgramGuide: '&',
                section: '<',
            },
            link(scope) {
                //-------------------------
                // Initialization
                //-------------------------
                NavigationHelperMixin.onLink(scope);
                const translationHelper = new TranslationHelper('settings.tuition_and_registration');

                Object.defineProperty(scope, 'currentUser', {
                    get() {
                        return $rootScope.currentUser;
                    },
                });

                scope.supportsBuyNowPayLater = getProgramConfigValue(
                    scope.currentUser.relevantCohort.programType,
                    'supportsBuyNowPayLater',
                );

                // A user should never see this page if their active AOI record isn't an AdmissionOffer.
                scope.admissionOffer = getAdmissionOffer(scope.currentUser);

                // While it is technically possible for an admission offer to have multiple cohort section offers
                // (in the sense that the data model supports it), it should not be possible for a user to be offered
                // admission with multiple cohort section offers going forward. We can safely look at the first element
                // in the array here since this UI only applies to folks who are offered admission.
                scope.cohortSectionOffer = scope.admissionOffer?.cohortSectionOffers[0];
                scope.numOfferedScholarships = scope.cohortSectionOffer?.offeredScholarships?.length ?? 0;

                // It shouldn't be possible to get here without a CohortSectionOffer or an AdmissionOffer.
                // If we do, we want to know about it. If this error is logged, the UI is definitely broken for this user.
                if (!scope.cohortSectionOffer) {
                    ErrorLogService.notifyInProd(
                        'Trying to render cohortRegistrationPlanSelection UI without a CohortSectionOffer',
                        undefined,
                        {
                            userId: scope.currentUser.id,
                        },
                    );
                    $rootScope.goHome('cohort_registration_plan_selection_dir::no_cohort_section_offer');
                }

                scope.isTwoMonthExecEd =
                    getProgramConfigValue(scope.currentUser.relevantCohort.programType, 'numMonths') === 2 &&
                    scope.currentUser.relevantCohort.startDate >= new Date('2026/06/08'); // cycle 77+

                scope.hasAiLaunchScholarship =
                    scope.isTwoMonthExecEd &&
                    scope.cohortSectionOffer?.offeredScholarships?.some(s => s.label === 'ai_launch_2026');

                scope.programInclusionProxy = {
                    shareable_with_classmates: !scope.isTwoMonthExecEd,
                };

                scope.selectedPaymentTypeProxy = { value: null };

                scope.PaymentView = PaymentView; // referenced in template
                scope.DISCUSS_OTHER_OPTIONS = DISCUSS_OTHER_OPTIONS; // referenced in template
                scope.activePaymentView = PaymentView.select_plan;

                scope.hasPartialScholarship = isPartialScholarship(
                    scope.cohortSectionOffer?.cumulativeOfferedScholarship,
                );

                scope.totalScholarshipForDisplay = formatCurrencyForDisplay(
                    scope.cohortSectionOffer?.cumulativeOfferedScholarship?.amount,
                );

                Object.defineProperty(scope, 'selectablePaymentSituations', {
                    get() {
                        return (scope.cohortSectionOffer?.studentSelectablePaymentSituations || [])
                            .map(studentSelectablePaymentSituation => {
                                const paymentSituation = paymentSituationWithFormattedValues(
                                    studentSelectablePaymentSituation,
                                );

                                let paymentOptionDetailsKey = `settings.tuition_and_registration.tuition_plan_detail_${paymentSituation.tuitionPlan.frequency}`;
                                if (paymentSituation.paymentInformation.netDiscountAmount > 0) {
                                    paymentOptionDetailsKey += '_with_discount';
                                }

                                return {
                                    ...paymentSituation,
                                    paymentOptionDescKey: `settings.tuition_and_registration.${paymentSituation.tuitionPlan.frequency}_payment_option_desc`,
                                    paymentOptionDescValues: {
                                        numIntervals: paymentSituation.tuitionPlan.numIntervals,
                                        paymentPerInterval:
                                            paymentSituation.paymentInformation.formattedPaymentAmountPerInterval,
                                    },
                                    paymentOptionDetailsKey,
                                    paymentOptionDetailsValues: {
                                        netDiscountAmount:
                                            paymentSituation.paymentInformation.formattedNetDiscountAmount,
                                        numIntervals: paymentSituation.tuitionPlan.numIntervals,
                                    },
                                };
                            })
                            .sort(
                                (a, b) =>
                                    TuitionPlanFrequencySortKey[a.tuitionPlan.frequency] -
                                    TuitionPlanFrequencySortKey[b.tuitionPlan.frequency],
                            );
                    },
                    configurable: true,
                });

                Object.defineProperty(scope, 'additionalFinancingOptions', {
                    get() {
                        return (scope.cohortSectionOffer?.additionalFinancingOptions || [])
                            .map(additionalFinancingOption => {
                                const formattedAdditionalFinancingOption = additionalFinancingOptionWithFormattedValues(
                                    scope.cohortSectionOffer.basePaymentSituation.paymentInformation.netRequiredPayment,
                                    additionalFinancingOption,
                                );
                                const paymentOptionDescKey =
                                    additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS
                                        ? 'additional_financing_payment_option_other_desc'
                                        : 'additional_financing_payment_option_desc';
                                const paymentOptionDetailsKey =
                                    additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS
                                        ? null
                                        : 'additional_financing_option_details';
                                return {
                                    ...formattedAdditionalFinancingOption,
                                    paymentOptionDescKey: `settings.tuition_and_registration.${paymentOptionDescKey}`,
                                    paymentOptionDescValues: {
                                        paymentPerInterval:
                                            formattedAdditionalFinancingOption.paymentInformation
                                                .formattedPaymentAmountPerInterval,
                                    },
                                    paymentOptionDetailsKey: paymentOptionDetailsKey
                                        ? `settings.tuition_and_registration.${paymentOptionDetailsKey}`
                                        : null,
                                    paymentOptionDetailsValues: {
                                        numIntervals:
                                            formattedAdditionalFinancingOption.additionalFinancingOption
                                                .monthlyDuration,
                                        apr: Math.round(
                                            formattedAdditionalFinancingOption.additionalFinancingOption.apr * 100,
                                        ),
                                    },
                                };
                            })
                            .sort((a, b) => {
                                if (a.additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS) {
                                    return 1;
                                }
                                if (b.additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS) {
                                    return -1;
                                }
                                return (
                                    a.additionalFinancingOption.monthlyDuration -
                                    b.additionalFinancingOption.monthlyDuration
                                );
                            });
                    },
                    configurable: true,
                });

                Object.defineProperty(scope, 'additionalFinancingOptionSelected', {
                    get() {
                        return scope.additionalFinancingOptions.find(
                            option =>
                                option.additionalFinancingOption.label ===
                                scope.paymentSelectionProxy.selectedPaymentOptionId,
                        );
                    },
                });

                Object.defineProperty(scope, 'additionalFinancingOptionIsSelected', {
                    get() {
                        return !!scope.additionalFinancingOptionSelected;
                    },
                });

                Object.defineProperty(scope, 'discussOtherOptionsAvailable', {
                    get() {
                        return scope.additionalFinancingOptions.some(
                            option => option.additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS,
                        );
                    },
                });

                scope.paymentOptions = [...scope.selectablePaymentSituations, ...scope.additionalFinancingOptions];

                scope.paymentSelectionProxy = {
                    selectedPaymentOptionId:
                        scope.paymentOptions[0]?.tuitionPlan?.id ||
                        scope.paymentOptions[0]?.additionalFinancingOption?.label,
                };

                //-------------------------
                // Payment Config
                //-------------------------

                scope.$watchGroup(
                    [
                        'paymentConfigState',
                        'paymentSelectionProxy.selectedPaymentOptionId',
                        'supportsBuyNowPayLater',
                        'currentUser.country',
                        'currentUser.state',
                    ],
                    ([paymentConfigState, selectedPaymentOptionId, supportsBuyNowPayLater, country, state]) => {
                        const selectedPaymentSituation = scope.selectablePaymentSituations.find(
                            situation => situation.tuitionPlan.id === selectedPaymentOptionId,
                        );

                        scope.paymentConfig = getPaymentConfig({
                            paymentConfigState,
                            selectedPaymentSituation,
                            section: scope.section,
                            supportsBuyNowPayLater,
                            country,
                            state,
                        });

                        if (!scope.paymentConfig) return;

                        const currentSelectedTypeIsAvailable = scope.paymentConfig
                            .availablePaymentTypeOptions()
                            .includes(scope.selectedPaymentTypeProxy.value);

                        // Preserve the current payment type selection if the type is still available per the new PaymentConfig
                        scope.selectedPaymentTypeProxy.value = currentSelectedTypeIsAvailable
                            ? scope.selectedPaymentTypeProxy.value
                            : getDefaultSelectedPaymentOption(scope.paymentConfig);
                    },
                );

                //-------------------------
                // Registration helpers
                //-------------------------

                attachDeferralModalFn(scope, $injector, scope.cohortSectionOffer?.cumulativeOfferedScholarship);
                attachOpenStripeCheckoutSessionFn(scope, $injector);
                attachRegisterButtonWatchFn(scope);
                attachRegisterEnabledWatchFn(scope);
                attachPaymentConfigStateWatchFn(scope);
                attachCohortDatesFn(scope);
                attachCohortRegistrationOptionsFn(scope);

                // If there are multiple Cohort options to register into, preselect the first.
                // It shouldn't be possible for scope.cohortRegistrationOptions to not have at least one option, for the offer Cohort,
                // but fallback to the offer Cohort if that's somehow happened.
                scope.registerForCohortIdProxy = {
                    value: scope.cohortRegistrationOptions[0]?.cohortId || scope.admissionOffer?.cohortId,
                };

                scope.showFundingEmail = false;

                scope.$watch('currentUser', currentUser => {
                    if (!currentUser) return;

                    scope.showFundingEmail = getProgramConfigValue(
                        currentUser.relevantCohort.programType,
                        'showFundingEmail',
                    );

                    // FIXME: Remove after magic link is disabled and offers are processed
                    if (scope.admissionOffer?.reportsTags?.includes('msba_launch_immediate_offer')) {
                        scope.showFundingEmail = false;
                    }

                    if (!scope.showFundingEmail) return;
                    const { fundingEmail } = targetBrandConfig(currentUser);

                    if (!fundingEmail) return;

                    scope.fundingEmail = fundingEmail;
                });

                //-------------------------
                // Locale Keys
                //-------------------------

                if (scope.hasPartialScholarship) {
                    scope.narrativeKey = 'settings.tuition_and_registration.narrative_partial_scholarship';
                    scope.graphicTitleKey = 'settings.tuition_and_registration.graphic_partial_scholarship_title';
                    scope.graphicSubtitleKey = 'settings.tuition_and_registration.graphic_partial_scholarship_subtitle';
                    scope.tuitionDetailsLabelKey = 'settings.tuition_and_registration.tuition_and_scholarship_details';
                } else {
                    scope.narrativeKey = 'settings.tuition_and_registration.narrative_no_scholarship';
                    scope.graphicTitleKey = 'settings.tuition_and_registration.graphic_no_scholarship_title';
                    scope.graphicSubtitleKey = 'settings.tuition_and_registration.graphic_no_scholarship_subtitle';
                    scope.tuitionDetailsLabelKey = 'settings.tuition_and_registration.tuition_details';
                }

                scope.planSelectionLabelKey = 'settings.tuition_and_registration.choose_your_plan';

                scope.smallPrintKey = () => {
                    let key = scope.hasPartialScholarship ? 'smallprint_scholarship' : 'smallprint';

                    // Extract cohort number from cohort name (e.g., "MBA74" -> "74")
                    const cohortName = scope.currentUser.relevantCohort?.name;
                    const cohortNumberMatch = cohortName?.match(/(\d+)$/);
                    const cohortNumber = cohortNumberMatch ? cohortNumberMatch[1] : null;

                    // Add cohort-specific suffix for cohorts 74 and 75
                    if (cohortNumber === '74' || cohortNumber === '75') {
                        key += `_cohort_${cohortNumber}`;
                    }

                    if (!scope.reapplicationDate) {
                        key += '_no_reapplication_date';
                    }

                    return `settings.tuition_and_registration.${key}`;
                };

                scope.formattedBaseTuition = paymentSituation =>
                    formatCurrencyForDisplay(paymentSituation.paymentInformation.baseTuition);
                //-------------------------
                // Checkout Session handling
                //-------------------------

                scope.handleRegisterButtonClick = () => {
                    const shouldOpenBankDetailsModal =
                        scope.activePaymentView === PaymentView.select_payment &&
                        scope.selectedPaymentTypeProxy.value === PaymentTypeOption.wire_transfer;

                    const shouldNavigateToSelectPayment =
                        scope.activePaymentView !== PaymentView.select_payment &&
                        scope.paymentConfig?.availablePaymentTypeOptions()?.length > 1 &&
                        !scope.registerRequiresRedirectToBrowser;

                    const shouldNavigateToFinancingOptionThankYouScreen =
                        scope.activePaymentView !== PaymentView.select_payment &&
                        scope.additionalFinancingOptionSelected;

                    if (shouldOpenBankDetailsModal) {
                        // do not take to Stripe, just provide wire transfer instructions
                        scope.openBankDetailsModal();
                        return;
                    }

                    if (shouldNavigateToSelectPayment) {
                        scope.activePaymentView = PaymentView.select_payment;
                        return;
                    }

                    if (shouldNavigateToFinancingOptionThankYouScreen) {
                        scope.activePaymentView = PaymentView.financing_option_selected;
                        const selectedFinancingOption = scope.additionalFinancingOptions.find(
                            option =>
                                option.additionalFinancingOption.label ===
                                scope.paymentSelectionProxy.selectedPaymentOptionId,
                        );
                        EventLogger.log(
                            'cohort_registration:financing_option_selected',
                            {
                                admission_offer_id: scope.admissionOffer.id,
                                cohort_id: scope.registerForCohortIdProxy.value,
                                program_type: scope.currentUser.relevantCohort.programType,
                                cohort_registration_deadline:
                                    dateHelper
                                        .shiftMonthDayForThreshold(
                                            scope.currentUser.relevantCohort.registrationDeadline,
                                        )
                                        .getTime() / 1000,
                                early_registration_deadline: scope.admissionOffer.earlyRegistrationDeadline
                                    ? dateHelper
                                          .shiftMonthDayForThreshold(
                                              new Date(scope.admissionOffer.earlyRegistrationDeadline * 1000),
                                          )
                                          .getTime() / 1000
                                    : null,
                                net_required_payment:
                                    scope.cohortSectionOffer.basePaymentSituation.paymentInformation.netRequiredPayment,
                                selected_financing_option: {
                                    label: selectedFinancingOption.additionalFinancingOption.label,
                                    monthly_duration: selectedFinancingOption.additionalFinancingOption.monthlyDuration,
                                    apr:
                                        Math.round(selectedFinancingOption.additionalFinancingOption.apr * 10000) / 100,
                                    payment_amount_per_interval:
                                        selectedFinancingOption.paymentInformation.formattedPaymentAmountPerInterval,
                                },
                            },
                            { log_to_customerio: true },
                        );
                        return;
                    }

                    // This is side effectful - it is guaranteed to navigate the user away from this page.
                    // If they're able to register in this environment, we'll send them to a Stripe Checkout Session.
                    // If they're unable to register in this environment (Cordova), we'll send them to a system browser.
                    scope.openStripeCheckoutSessionOrBrowser(scope, $injector, {
                        admission_offer_id: scope.admissionOffer.id,
                        cohort_id: scope.registerForCohortIdProxy.value,
                        payment_type: {
                            [PaymentTypeOption.us_bank_account]: 'ach',
                            [PaymentTypeOption.card]: 'card',
                            [PaymentTypeOption.buy_now_pay_later]: 'bnpl',
                        }[scope.selectedPaymentTypeProxy.value],
                        product_id: getProduct(scope.admissionOffer.programType).id,
                        selected_tuition_plan_id: scope.paymentSelectionProxy.selectedPaymentOptionId,
                        shareable_with_classmates: scope.programInclusionProxy.shareable_with_classmates,
                    });
                };

                //-------------------------
                // Button handlers
                //-------------------------

                scope.openMailToLink = () => {
                    const subjectString = 'Request for wire transfer bank details';
                    const bodyString =
                        'Hello, can you please pass along the details to initiate a bank/wire transfer for my Quantic tuition? Thank you!';

                    window.open(
                        `mailto:billing@quantic.edu?subject=${encodeURI(subjectString)}&body=${encodeURI(bodyString)}`,
                    );
                };

                scope.backToPlanSelection = () => {
                    scope.activePaymentView = PaymentView.select_plan;
                };

                scope.openOptionsModal = () => {
                    let content = '';
                    let hasAddedMonthlyPlanExplanation = false;

                    scope.paymentOptions.forEach(paymentOption => {
                        if (
                            // NOTE: we have other plans with a monthly frequency - the "accelerated" plans.
                            // We do not lump those in with the explanation of the regular monthly and extended monthly plans.
                            // They have their own plan explanations.
                            [TuitionPlanFrequency.Monthly, TuitionPlanFrequency.MonthlyExtended].includes(
                                paymentOption.tuitionPlan?.frequency,
                            ) ||
                            paymentOption.additionalFinancingOption
                        ) {
                            if (hasAddedMonthlyPlanExplanation) return;

                            content += `<h3 translate-once="settings.tuition_and_registration.monthly_options"></h3>\n<p translate-once="settings.tuition_and_registration.monthly_plan_explanation"></p>\n`;
                            hasAddedMonthlyPlanExplanation = true;
                            return;
                        }

                        let paymentTextKey = `${paymentOption.tuitionPlan.frequency}_plan_explanation`;
                        if (paymentOption.paymentInformation.netDiscountAmount > 0) {
                            paymentTextKey += '_with_discount';
                        }
                        const paymentText = translationHelper.get(paymentTextKey, {
                            additionalSavings: paymentOption.paymentInformation.formattedNetDiscountAmount,
                            numIntervals: paymentOption.tuitionPlan.numIntervals,
                        });
                        content += `<h3 translate-once="settings.tuition_and_registration.${paymentOption.tuitionPlan.frequency}_option"></h3>\n<p>${paymentText}</p>\n`;
                    });

                    const refundPolicyText = translationHelper.get('refund_policy');
                    content += `<p class="refund-policy">${refundPolicyText}</p>\n`;

                    DialogModal.alert({
                        title: translationHelper.get('payment_options'),
                        content,
                        classes: ['emba-billing-options', 'billing-options'],
                        scope: {
                            loadFaqUrl: NavigationHelperMixin.loadFaqUrl,
                        },
                    });
                };

                scope.openBankDetailsModal = () => {
                    // Some copy is untranslated because the user needs the exact bank data
                    const content = `
                        <p>${translationHelper.get(
                            `bank_details_intro${scope.selectablePaymentSituations.length === 1 ? '_1_pay_only' : ''}`,
                        )}</p>
                        <dl>
                            <dt>${translationHelper.get('our_bank_heading').toLocaleUpperCase()}</dt>
                            <dd>Bridge Bank, a Division of Western Alliance<br />55 Almaden Blvd, San Jose, CA 95113<br />USA</dd>
                            <dt>${translationHelper.get('our_account_name_heading').toLocaleUpperCase()}</dt>
                            <dd>Quantic Holdings Inc dba Quantic School of Business and Technology</dd>
                            <dt>${translationHelper.get('our_account_number_heading').toLocaleUpperCase()}</dt>
                            <dd>8593474229</dd>
                            <dt>${translationHelper.get('for_us_wires_heading').toLocaleUpperCase()}</dt>
                            <dd>${translationHelper.get('for_us_wires_content', { transitNumber: '121143260' })}</dd>
                            <dt>${translationHelper.get('for_international_wires_heading').toLocaleUpperCase()}</dt>
                            <dd>SWIFT ID: BBFXUS6S</dd>
                            <dt>
                                ${translationHelper
                                    .get('quantic_mailing_address_heading', { institutionName: 'Quantic' })
                                    .toLocaleUpperCase()}
                            </dt>
                            <dd>712 H Street NE Suite 1802<br />Washington, DC 20002<br />USA</dd>
                        </dl>
                        <p>${translationHelper.get('identifier')}</p>
                        <p>${translationHelper.get('contact_billing_with_questions', {
                            billingEmail: scope.currentUser.relevantCohort.supportEmailAddress('billing'),
                        })}</p>
                    `;
                    DialogModal.alert({
                        title: translationHelper.get('wiring_funds'),
                        content,
                        classes: ['bank-details'],
                    });
                };
            },
        };
    },
]);
